/**
 * Sign-in page — Untitled UI's `LoginCardCombined` layout, trimmed.
 *
 * Differences vs the upstream template:
 *   - Brand logo via atlas's `BrandLogo` (Orion / Gemini / Centaur swap).
 *   - No "Remember for 30 days" checkbox (we don't surface that toggle).
 *   - No "Sign in with Google" — backend doesn't support OAuth providers.
 *   - No "Don't have an account? Sign up" — sign-up is admin-only.
 *   - Form is controlled (state-backed) so we can render the inline error
 *     and disable Submit while the API is mid-flight.
 */
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { BrandLogo, Button, Input } from "@tricura/atlas-ui";
import { useAuth } from "./auth-context";

interface LocationStateWithFrom {
    from?: { pathname?: string };
}

export function LoginPage() {
    const { status, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Already signed in → bounce away.
    if (status === "authenticated") {
        const dest = (location.state as LocationStateWithFrom | null)?.from?.pathname || "/submissions";
        return <Navigate to={dest} replace />;
    }

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email.trim() || !password) {
            setError("Enter your email and password to continue.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            await login(email, password);
            const dest = (location.state as LocationStateWithFrom | null)?.from?.pathname || "/submissions";
            navigate(dest, { replace: true });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Sign in failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="bg-primary sm:bg-secondary min-h-screen px-4 py-12 md:px-8 md:pt-24">
            <div className="bg-primary sm:max-w-110 flex w-full flex-col gap-6 sm:mx-auto sm:rounded-2xl sm:px-10 sm:py-8 sm:shadow-sm">
                <div className="flex flex-col items-center gap-6 text-center">
                    <BrandLogo />
                    <div className="flex flex-col gap-2">
                        <h1 className="text-display-xs text-primary font-semibold">Welcome back</h1>
                        <p className="text-md text-tertiary">Please enter your details.</p>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="flex flex-col gap-6">
                    <div className="flex flex-col gap-5">
                        <Input
                            isRequired
                            type="email"
                            name="email"
                            size="md"
                            placeholder="Enter your email"
                            autoComplete="email"
                            value={email}
                            onChange={setEmail}
                            data-autotest="login-form-email-input"
                        />
                        <Input
                            isRequired
                            type="password"
                            name="password"
                            size="md"
                            placeholder="••••••••"
                            autoComplete="current-password"
                            value={password}
                            onChange={setPassword}
                            data-autotest="login-form-password-input"
                        />
                    </div>

                    <div className="flex items-center">
                        <Button color="link-color" size="md" href="/forgot-password" className="ml-auto" data-autotest="login-form-forgot-password-button">
                            Forgot password
                        </Button>
                    </div>

                    {error && (
                        <p role="alert" className="text-error-primary text-sm" data-autotest="login-form-error-message" >
                            {error}
                        </p>
                    )}

                    <div className="flex flex-col gap-4">
                        <Button type="submit" size="lg" isLoading={submitting} showTextWhileLoading data-autotest="login-form-submit-button">
                            Sign in
                        </Button>
                    </div>
                </form>
            </div>
        </section>
    );
}
